import React from 'react';
import styled from '@emotion/styled';
import { Box, Typography, Link, Chip, Tooltip } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { targetHeading, targetTypeLabel, targetDisplayName, targetLink } from '../utils/suggestionUtils';

const Wrapper = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
});

const Heading = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.25),
}));

const NameRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
}));

const TargetLink = styled(Link)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontWeight: theme.typography.fontWeightMedium,
    wordBreak: 'break-word',
}));

const LinkIcon = styled(OpenInNew)(({ theme }) => ({
    fontSize: `${theme.typography.size.md} !important`,
}));

const PlainName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    wordBreak: 'break-word',
}));

const MissingName = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
}));

const ContextText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const SuggestionTarget = ({ actionType, target, showHeading = true }) => {
    if (!target) {
        return (
            <Wrapper>
                {showHeading && <Heading>Applies to</Heading>}
                <MissingName variant="body2">Not tied to a specific item.</MissingName>
            </Wrapper>
        );
    }

    const typeLabel = targetTypeLabel(target.type);
    const name = targetDisplayName(target);
    const link = targetLink(target);

    return (
        <Wrapper>
            {showHeading && <Heading>{targetHeading(actionType, target)}</Heading>}
            <NameRow>
                {!target.exists ? (
                    <Tooltip title="It was deleted after this suggestion was made, so there is nothing to open.">
                        <MissingName variant="body2">{name}</MissingName>
                    </Tooltip>
                ) : link ? (
                    <Tooltip title={`Open this ${typeLabel.toLowerCase()} in a new tab`}>
                        <TargetLink href={link} target="_blank" rel="noopener noreferrer" variant="body2" underline="hover">
                            {name}
                            <LinkIcon />
                        </TargetLink>
                    </Tooltip>
                ) : (
                    <PlainName variant="body2">{name}</PlainName>
                )}
                {target.exists && target.status && <Chip label={target.status} size="small" variant="outlined" />}
            </NameRow>
            <ContextText variant="caption">
                {typeLabel}
                {target.exists && target.context ? ` · ${target.context}` : ''}
            </ContextText>
        </Wrapper>
    );
};

export default SuggestionTarget;
